import { useId } from 'react';
import type { JSX, ReactNode } from 'react';
import { STATUS_TONES, cx } from './styles';
import type { StatusLevel } from './styles';

export interface PanelProps {
  title?: ReactNode;
  description?: ReactNode;
  /** 头部右侧的操作区 */
  actions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * 正文自己滚动，头部与底部固定。
   * 生效前提：Panel 被放进**有确定高度**的父容器（`flex-1 min-h-0` 之类）——
   * 高度不确定时 `overflow-auto` 不会滚，会直接把页面撑长。
   */
  scroll?: boolean;
  /** 撑满父容器高度（配合 `scroll` 才是"标准操作台面板"） */
  fill?: boolean;
  headingLevel?: 2 | 3;
  level?: StatusLevel;
  className?: string;
}

function PanelHeading({ level, id, children }: { level: 2 | 3; id: string; children: ReactNode }): JSX.Element {
  if (level === 2) {
    return (
      <h2 id={id} className="m-0 text-sm font-semibold tracking-tight text-fg">
        {children}
      </h2>
    );
  }
  return (
    <h3 id={id} className="m-0 text-sm font-semibold tracking-tight text-fg">
      {children}
    </h3>
  );
}

/**
 * 结构面板：页面上"一块固定的区域"。
 *
 * 和 `Card` 的区别在职责：Panel 参与布局（`fill` 撑满高度、头部固定、正文滚动），
 * Card 只做视觉分组。参数列表、任务列表、日志这类"内容可能比屏幕长"的东西用 Panel。
 */
export function Panel({
  title,
  description,
  actions,
  children,
  footer,
  scroll = false,
  fill = false,
  headingLevel = 3,
  level,
  className,
}: PanelProps): JSX.Element {
  const headingId = `panel-${useId().replaceAll(':', '')}`;
  const hasHeader = title !== undefined || description !== undefined || actions !== undefined;

  return (
    <section
      {...(title === undefined ? {} : { 'aria-labelledby': headingId })}
      className={cx(
        'flex min-h-0 flex-col overflow-hidden rounded-card border border-line bg-surface-1 shadow-card',
        level === undefined ? undefined : cx('border-l-2', STATUS_TONES[level].borderLeft),
        fill && 'h-full flex-1',
        className,
      )}
    >
      {hasHeader ? (
        /*
         * 头部给一层浅底（`bg-surface-2`）：面板常常叠在卡片里，只靠一条分隔线
         * 不足以让人一眼分清"哪里是标题、哪里是内容"。一层浅底把这条边界做成**面**，
         * 而不是一条 1px 的线 —— 户外屏上 1px 的线是最先被看丢的东西。
         */
        <header className="flex shrink-0 items-start gap-3 border-b border-line bg-surface-2 px-3.5 py-2.5">
          <div className="min-w-0 flex-1">
            {title === undefined ? null : (
              <PanelHeading level={headingLevel} id={headingId}>
                {title}
              </PanelHeading>
            )}
            {description === undefined ? null : (
              <p className="m-0 mt-0.5 text-xs text-fg-subtle">{description}</p>
            )}
          </div>
          {actions === undefined ? null : (
            <div className="flex shrink-0 items-center gap-1">{actions}</div>
          )}
        </header>
      ) : null}

      <div className={cx('min-h-0 flex-1 px-3.5 py-2.5', scroll && 'overflow-auto')}>{children}</div>

      {footer === undefined ? null : (
        <footer className="shrink-0 border-t border-line px-3.5 py-2 text-xs text-fg-muted">
          {footer}
        </footer>
      )}
    </section>
  );
}
